import { type Query, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
	normalizeRealtimeTopic,
	subscribeMesfRealtimeTopic,
} from "../services/mesfRealtimeConnection";

/**
 * Query meta that opts a query into realtime invalidation:
 * `useQuery({ queryKey, queryFn, meta: { realtime: ["M2.GetHeats"] } })`.
 * The query is invalidated whenever the server reports one of these topics as stale.
 */
export type RealtimeQueryMeta = { realtime?: string | string[] };

const getQueryTopics = (query: Query): string[] => {
	const realtime = (query.meta as RealtimeQueryMeta | undefined)?.realtime;
	const topics = typeof realtime === "string" ? [realtime] : realtime;
	if (!Array.isArray(topics)) return [];
	return [...new Set(topics.filter(Boolean).map(normalizeRealtimeTopic))];
};

/**
 * Keeps the realtime topic subscriptions in line with the queries in the cache. A query stays
 * subscribed until it is garbage collected (not just unmounted), so cached data never goes
 * stale silently while the page is closed.
 */
export const useRealtimeQueryInvalidation = () => {
	const queryClient = useQueryClient();

	useEffect(() => {
		const cache = queryClient.getQueryCache();
		const topicsByQuery = new Map<string, string[]>();
		const topicRefs = new Map<
			string,
			{ count: number; unsubscribe: () => void }
		>();

		const invalidateTopic = (topic: string) => {
			void queryClient.invalidateQueries({
				predicate: (query) => getQueryTopics(query).includes(topic),
			});
		};

		const acquire = (topic: string) => {
			const ref = topicRefs.get(topic);
			if (ref) {
				ref.count++;
				return;
			}
			topicRefs.set(topic, {
				count: 1,
				unsubscribe: subscribeMesfRealtimeTopic(topic, invalidateTopic),
			});
		};

		const release = (topic: string) => {
			const ref = topicRefs.get(topic);
			if (!ref) return;
			if (--ref.count === 0) {
				ref.unsubscribe();
				topicRefs.delete(topic);
			}
		};

		const track = (query: Query) => {
			const next = getQueryTopics(query);
			const prev = topicsByQuery.get(query.queryHash) ?? [];
			if (next.join("\n") === prev.join("\n")) return;
			next.forEach(acquire);
			prev.forEach(release);
			if (next.length) topicsByQuery.set(query.queryHash, next);
			else topicsByQuery.delete(query.queryHash);
		};

		const untrack = (query: Query) => {
			topicsByQuery.get(query.queryHash)?.forEach(release);
			topicsByQuery.delete(query.queryHash);
		};

		cache.getAll().forEach(track);
		const unsubscribeCache = cache.subscribe((event) => {
			if (event.type === "removed") untrack(event.query);
			else track(event.query);
		});

		return () => {
			unsubscribeCache();
			topicRefs.forEach((ref) => ref.unsubscribe());
			topicRefs.clear();
			topicsByQuery.clear();
		};
	}, [queryClient]);
};

export const RealtimeQueryInvalidation = () => {
	useRealtimeQueryInvalidation();
	return null;
};
